import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Briefcase, ChevronLeft } from 'lucide-react';
import StepAccount from './professional-registration/StepAccount';
import StepIdentity from './professional-registration/StepIdentity';
import StepArea from './professional-registration/StepArea';
import StepServices from './professional-registration/StepServices';
import StepBank from './professional-registration/StepBank';
import StepReview from './professional-registration/StepReview';

const STEPS = ['Account', 'Identity', 'Area', 'Services & Specialties', 'Bank Details', 'Review'];

const initialData = {
  name: '',
  phone: '',
  idNumber: '',
  idDocumentFile: null,
  selfieFile: null,
  province: '',
  city: '',
  serviceRadiusKm: 20,
  services: [],
  bankName: '',
  bankAccountNumber: '',
  bankAccountHolder: '',
};

const validateStep = (step, data) => {
  if (step === 1) {
    if (!data.name.trim()) return 'Full name / business name is required.';
    if (!data.phone.trim()) return 'Phone number is required.';
    if (!data.idNumber.trim()) return 'SA ID number or passport number is required.';
    if (!data.idDocumentFile) return 'Please upload a photo of your ID document.';
    if (!data.selfieFile) return 'Please upload a selfie photo.';
  }
  if (step === 2) {
    if (!data.province) return 'Please select a province.';
    if (!data.city.trim()) return 'City / suburb is required.';
    if (!data.serviceRadiusKm || data.serviceRadiusKm < 1) return 'Please set a service radius.';
  }
  if (step === 3) {
    if (data.services.length === 0) return 'Please add at least one service.';
    for (const s of data.services) {
      if (!s.pricing_range || !s.pricing_range.trim()) return 'Every service needs a pricing range.';
    }
  }
  return '';
};

const RegisterProfessional = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ ...initialData, name: user?.name || '' });
  const [stepError, setStepError] = useState('');

  const updateData = (patch) => setData((prev) => ({ ...prev, ...patch }));

  const goNext = () => {
    const err = validateStep(step, data);
    if (err) { setStepError(err); return; }
    setStepError('');
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setStepError('');
    setStep((s) => Math.max(s - 1, 0));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-block bg-[#37b34a]/10 rounded-full p-4 mb-4">
            <Briefcase size={32} className="text-[#37b34a]" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Become a Professional</h1>
          <p className="text-base text-gray-600">Tell us about yourself, your services, and your area</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-testid="pro-registration-wizard">
          {/* Step indicator */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs font-semibold text-gray-400 mb-2 overflow-x-auto gap-2">
              {STEPS.map((label, i) => (
                <span key={label} data-testid={`wizard-step-label-${i}`} className={`whitespace-nowrap ${i === step ? 'text-[#37b34a]' : i < step ? 'text-gray-600' : ''}`}>
                  {i + 1}. {label}
                </span>
              ))}
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#37b34a] transition-all duration-300" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
            </div>
          </div>

          {step > 0 && (
            <button type="button" onClick={goBack} data-testid="wizard-back-btn" className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">
              <ChevronLeft size={16} /> Back
            </button>
          )}

          {stepError && (
            <div data-testid="wizard-step-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-4">
              {stepError}
            </div>
          )}

          {step === 0 && <StepAccount onNext={goNext} />}
          {step === 1 && <StepIdentity data={data} onChange={updateData} />}
          {step === 2 && <StepArea data={data} onChange={updateData} />}
          {step === 3 && <StepServices services={data.services} onChange={(services) => updateData({ services })} />}
          {step === 4 && <StepBank data={data} onChange={updateData} />}
          {step === 5 && <StepReview data={data} user={user} onEditStep={setStep} />}

          {step >= 1 && step <= 4 && (
            <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
              <button
                type="button" onClick={() => navigate('/')}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button" onClick={goNext} data-testid="wizard-next-btn"
                className="flex-1 bg-[#37b34a] hover:bg-[#2d9a3e] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                {step === 4 ? (data.bankName || data.bankAccountNumber || data.bankAccountHolder ? 'Continue' : 'Skip for now') : 'Continue'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterProfessional;
